import React, { useState, useEffect, forwardRef, useRef } from "react";
import formatDistanceToNowStrict from "date-fns/formatDistanceToNowStrict";
import format from "date-fns/format";
import styled from "styled-components";
import DynamicComponent from "../DynamicComponent";
import Image from "../Image";
import classNames from "classnames";
import IStyledProps from "../../utilities/IStyledProps";
import IEditableProps from "../../utilities/IEditableProps";
import { usePageProps } from '../../context/PagePropContext';

const NewsroomContainer = styled.div<{lines: number}>`

    @media(min-width:1440px) {
        .newsroom-content.px-4 {
            padding-left: 3rem !important;
            padding-right: 3rem !important;
        }
    }
    .text-black {
        color: #000 !important;
    }
    .newsroom-post-age {
        font-size: 0.778rem;
    }
    .newsroom-title:hover {
        text-decoration: underline;
        color: #720b22 !important;
    }
    .newsroom-text-truncate  {
        -webkit-line-clamp: ${props => props.lines};
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
    }

`;

function NewsroomLink({ href, children, ...props }) {
    if (href) {
        return (
            <a href={href} className="text-decoration-none" {...props}>
                {children}
            </a>
        );
    }

    return children;
}

export interface INewsroomProps extends IStyledProps, IEditableProps {
    dateType?: string;
    dateFormat?: string;
    src?: string;
    alt?: string;
    description?: string;
    content?: string;
    headerAs?: string;
    href?: string;
    title: string;
    timestamp: number | string;
    testIdPrefix?: string;
}

const Newsroom = forwardRef((props: INewsroomProps, ref: any) => {
    const { cmsId, editorClassObj, headerAs = "big", description, content, title, timestamp: newsroomPropsTimestamp, src, alt, href, dateType = "date", dateFormat = "MMMM d, yyyy", testIdPrefix = '' } = props;
    const { timestamp: pagePropsTimestamp, newsroomTitle, imageSrc, imageAlt } = usePageProps(); // these props come in separate from 'props' because they need to be in frontmatter for meta tags
    const displaytitle = title ?? newsroomTitle;
    const displayDate = newsroomPropsTimestamp ?? pagePropsTimestamp;
    const displaySrc = src ?? imageSrc;
    const displayAlt = alt ?? imageAlt;
    const headersMap = { big: 'h1', small: 'h5' };
    const headerTag = headersMap[headerAs] || headerAs;

    const [postAge, setPostAge] = useState(calculateDate(displayDate, dateType));
    const [contentLines, setContentLines] = useState(0);
    const contentRef = useRef(null);

    useEffect(() => {
        const lineHeight = 1.5; //rem
        const maxContentHeight = 10.5; //rem
        const headingHeight = contentRef.current.scrollHeight/18; //rem
        const calculatedLines = Math.floor(((maxContentHeight - headingHeight) / lineHeight));

        // Sometimes it can calculate less lines, and it looks weird. I think 2 is a good minimum.
        setContentLines(calculatedLines < 2 ? 2 : calculatedLines);
    }, [ref]);

    function getDate(timestamp: number|string) {
        const dateNumber = Number(timestamp);
        return new Date(isNaN(dateNumber) ? timestamp : dateNumber);
    }

    function calculateDate(date: string, type: string) {
        try {
            if (!date) {
                return "";
            }
            else if (type === "timeAgo") {
                const formattedPostAge = formatDistanceToNowStrict(getDate(date), { addSuffix: true });
                return formattedPostAge.toUpperCase();
            } else {
                const formattedDate = format(getDate(date), dateFormat);
                return formattedDate;
            }
        } catch (err) {
            console.warn(`Newsroom: error while parsing date ${date}`);
            return "";
        }
    }

    useEffect(() => {
        setPostAge(calculateDate(displayDate, dateType));
    }, [displayDate, dateType]);

    return (
        <div ref={ref} data-id={cmsId} className={classNames(editorClassObj, 'mb-4')}>
            <NewsroomContainer lines={contentLines}>
                <NewsroomLink data-test-id={`${testIdPrefix}newsroom-title-link`} href={href} className="text-decoration-none">
                    <div className="row text-decoration-none">
                        <div className="offset-lg-1 col-lg-4 col-12 px-4">
                            {displaySrc && <Image dataTestId="newsroom-image" src={displaySrc} alt={displayAlt} />}
                        </div>
                        <div className="px-4 col-lg-6 mt-4 mt-lg-0">
                            <div ref={contentRef}>
                                <DynamicComponent data-test-id={`${testIdPrefix}newsroom-title`} as={headerTag} className="text-primary mb-0 newsroom-title">
                                    {displaytitle}
                                </DynamicComponent>
                                <div className="pt-2 text-black">
                                    <div data-test-id="newsroom-date" className="newsroom-post-age">
                                        {postAge}
                                    </div>
                                </div>
                                {description &&
                                    <div data-test-id="newsroom-description" className="mt-2 mt-3 font-italic text-black">
                                        <p>{description}</p>
                                    </div>
                                }
                            </div>
                            {content &&
                                <div data-test-id="newsroom-content" className="mt-2 mt-3 text-black">
                                    <p className="newsroom-text-truncate">{content}</p>
                                </div>
                            }
                        </div>
                    </div>
                </NewsroomLink>
            </NewsroomContainer>
            <div className="row">
                <div className="offset-lg-1 col-lg-10">
                        <hr className="my-4" />
                </div>
            </div>
        </div>
    );
});

export default Newsroom;